<script setup>
// Conexão + ferramentas de um servidor MCP interno: mostra a URL do endpoint e
// o token (copiáveis) p/ o usuário plugar num cliente de IA externo, e lista as
// ferramentas expostas pelas capacidades marcadas.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
});
defineEmits(['close']);

const { t } = useI18n();
const revealToken = ref(false);

const tools = computed(() => props.server.tools || []);
const maskedToken = computed(() => {
  const tk = props.server.token || '';
  return revealToken.value ? tk : `${tk.slice(0, 8)}${'•'.repeat(16)}`;
});

const copy = async (value, labelKey) => {
  try {
    await navigator.clipboard.writeText(value);
    useAlert(t(labelKey));
  } catch (e) {
    useAlert(value);
  }
};

const inputKeys = tool => {
  const schema = tool.inputSchema || tool.input_schema || {};
  const p = schema.properties || {};
  const required = schema.required || [];
  return Object.keys(p).map(k => ({
    name: k,
    type: p[k]?.type || 'any',
    required: required.includes(k),
  }));
};
</script>

<template>
  <div
    class="flex flex-col h-auto max-h-[80vh] overflow-auto w-[34rem] max-w-full"
  >
    <woot-modal-header
      :header-title="server.name"
      :header-content="t('MCP_MGMT.DETAILS.TITLE')"
    />

    <div class="flex flex-col gap-4 px-8 py-4">
      <!-- como conectar: URL + token -->
      <div
        class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak bg-n-alpha-1"
      >
        <p class="text-xs text-n-slate-10">{{ t('MCP_MGMT.DETAILS.HOWTO') }}</p>

        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('MCP_MGMT.DETAILS.URL') }}
          </span>
          <div class="flex items-center gap-2">
            <code
              class="flex-1 px-2 py-1.5 text-xs truncate rounded bg-n-alpha-2 text-n-slate-12"
            >
              {{ server.endpoint_url }}
            </code>
            <NextButton
              icon="i-lucide-copy"
              slate
              sm
              @click="copy(server.endpoint_url, 'MCP_MGMT.DETAILS.COPIED_URL')"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('MCP_MGMT.DETAILS.TOKEN') }}
          </span>
          <div class="flex items-center gap-2">
            <code
              class="flex-1 px-2 py-1.5 text-xs truncate rounded bg-n-alpha-2 text-n-slate-12"
            >
              {{ maskedToken }}
            </code>
            <NextButton
              :icon="revealToken ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              slate
              sm
              @click="revealToken = !revealToken"
            />
            <NextButton
              icon="i-lucide-copy"
              slate
              sm
              @click="copy(server.token, 'MCP_MGMT.DETAILS.COPIED_TOKEN')"
            />
          </div>
          <span class="text-xs text-n-slate-9">
            {{ t('MCP_MGMT.DETAILS.AUTH_HINT') }}
          </span>
        </div>
      </div>

      <!-- ferramentas expostas -->
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('MCP_MGMT.DETAILS.TOOLS') }}
        </span>
        <p v-if="!tools.length" class="text-sm text-n-slate-10">
          {{ t('MCP_MGMT.DETAILS.NO_TOOLS') }}
        </p>
        <div
          v-for="tool in tools"
          :key="tool.name"
          class="flex flex-col gap-1.5 p-3 border rounded-lg border-n-weak"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-wrench size-3.5 text-n-brand" />
            <code class="text-sm font-semibold text-n-slate-12">
              {{ tool.name }}
            </code>
          </div>
          <p v-if="tool.description" class="text-sm text-n-slate-11">
            {{ tool.description }}
          </p>
          <div
            v-if="inputKeys(tool).length"
            class="flex flex-wrap gap-1 mt-0.5"
          >
            <span class="text-xs text-n-slate-10 ltr:mr-1 rtl:ml-1">
              {{ t('MCP_MGMT.DETAILS.INPUTS') }}:
            </span>
            <span
              v-for="input in inputKeys(tool)"
              :key="input.name"
              class="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ input.name
              }}<span class="text-n-slate-9">:{{ input.type }}</span>
              <span v-if="input.required" class="text-n-ruby-11">*</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-end w-full gap-2 px-8 py-4">
      <NextButton
        faded
        slate
        :label="t('MCP_MGMT.DETAILS.CLOSE')"
        @click="$emit('close')"
      />
    </div>
  </div>
</template>
